import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Share2, Copy, Check, Users, Phone, Loader2, MessageSquare, Printer } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../store/authStore';

/**
 * InviteParentsDialog — generates a shareable /join/:teamId link the coach can text,
 * plus an optional bulk phone pre-approval field that auto-links parents upon signup.
 */
export default function InviteParentsDialog({ open, onOpenChange, teamId, teamName }) {
  const [copied, setCopied] = useState(false);
  const [phoneText, setPhoneText] = useState('');
  const [preapproving, setPreapproving] = useState(false);
  const [approvedList, setApprovedList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [printing, setPrinting] = useState(false);
  const { token } = useAuthStore();

  const joinUrl = `${window.location.origin}/join/${teamId}`;
  const shareText = `You're invited to follow ${teamName} on Diamond Scorer! Tap here to get live scores, push alerts for big plays, and messages from the coach: ${joinUrl}`;

  useEffect(() => {
    if (!open || !teamId) return;
    loadApproved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamId]);

  const loadApproved = async () => {
    setLoadingList(true);
    try {
      const r = await api.get(`/teams/${teamId}/preapproved`);
      setApprovedList(r.data || []);
    } catch {}
    setLoadingList(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied!');
    } catch {
      toast.error('Could not copy');
    }
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Follow ${teamName}`, text: shareText, url: joinUrl });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success('Message copied — paste it into your team text!');
      }
    } catch {}
  };

  const submitPreapprove = async () => {
    const lines = phoneText.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    if (lines.length === 0) return toast.error('Paste one or more phone numbers');
    setPreapproving(true);
    try {
      const r = await api.post(`/teams/${teamId}/preapprove-phones`, { phones: lines });
      toast.success(`${r.data.added} phone${r.data.added === 1 ? '' : 's'} added`);
      setPhoneText('');
      loadApproved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pre-approve');
    } finally { setPreapproving(false); }
  };

  const downloadFlyer = async () => {
    setPrinting(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${base}/api/teams/${teamId}/flyer.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        throw new Error(errText || `Failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diamond-scorer-flyer-${(teamName || 'team').replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Flyer downloaded! Print and pin to the dugout.');
    } catch (err) {
      toast.error('Could not generate flyer');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="invite-parents-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} className="text-green-700" /> Invite parents to {teamName}
          </DialogTitle>
          <DialogDescription>
            Text this link to your team&apos;s group chat. When parents tap it, they create a one-time account and start getting live scores, push alerts, and messages.
          </DialogDescription>
        </DialogHeader>

        {/* Share link */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2" data-testid="invite-link-section">
          <Label className="text-[11px] uppercase tracking-wider text-green-700 font-bold">Share link</Label>
          <div className="flex gap-2">
            <Input readOnly value={joinUrl} className="font-mono text-xs bg-white" data-testid="invite-link-input" />
            <Button size="sm" variant="outline" onClick={copyLink} className="shrink-0 border-green-600" data-testid="invite-copy-btn">
              {copied ? <><Check size={14} className="mr-1" /> Copied</> : <><Copy size={14} className="mr-1" /> Copy</>}
            </Button>
          </div>
          <Button className="w-full bg-green-700 hover:bg-green-600 text-white" onClick={shareLink} data-testid="invite-share-btn">
            <Share2 size={14} className="mr-2" /> Share to team group text
          </Button>
          <Button
            variant="outline"
            className="w-full border-green-700 text-green-800 hover:bg-green-100"
            onClick={downloadFlyer}
            disabled={printing}
            data-testid="invite-print-flyer-btn"
          >
            {printing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Printer size={14} className="mr-2" />}
            Print flyer (PDF with QR code)
          </Button>
          <p className="text-[11px] text-stone-500 text-center">
            Pin it to the dugout fence — parents scan with their camera to join instantly.
          </p>
        </div>

        {/* Bulk preapprove */}
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-wider text-stone-700 font-bold flex items-center gap-1">
            <Phone size={11} /> Or pre-approve phone numbers (optional)
          </Label>
          <textarea
            rows={3}
            className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm font-mono"
            placeholder={'5551234567\n(555) 987-6543\n...'}
            value={phoneText}
            onChange={e => setPhoneText(e.target.value)}
            data-testid="invite-bulk-input"
          />
          <p className="text-[11px] text-stone-500">
            Paste one per line or comma-separated. When any of these sign up, they&apos;re automatically linked to {teamName}.
          </p>
          <Button size="sm" variant="outline" onClick={submitPreapprove} disabled={preapproving} className="w-full" data-testid="invite-preapprove-submit">
            {preapproving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
            Pre-approve phones
          </Button>

          {/* List */}
          {loadingList ? (
            <p className="text-stone-400 text-xs text-center py-1">Loading…</p>
          ) : approvedList.length > 0 && (
            <div className="border border-stone-200 rounded-md divide-y divide-stone-100 max-h-40 overflow-y-auto" data-testid="invite-approved-list">
              {approvedList.map(row => (
                <div key={row.id} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`invite-row-${row.phone_number}`}>
                  <span className="font-mono text-stone-700 flex-1">{row.phone_number}</span>
                  {row.redeemed ? (
                    <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">JOINED</span>
                  ) : (
                    <span className="text-[10px] font-bold bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">WAITING</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[11px] text-stone-400 text-center">
          <MessageSquare size={10} className="inline mr-1" />
          Parents who joined will see your messages in their app — no SMS fees needed.
        </p>
      </DialogContent>
    </Dialog>
  );
}
